import React, { useState, useEffect } from 'react';
import {
    EuiPanel,
    EuiText,
    EuiButton,
    EuiFlexGroup,
    EuiFlexItem,
    EuiSpacer,
    EuiLink,
    EuiPageBody,
    EuiHorizontalRule,
    EuiLoadingSpinner,
    EuiModal,
    EuiModalHeader,
    EuiModalHeaderTitle,
    EuiModalBody,
    EuiModalFooter,
} from '@elastic/eui';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import APIV1 from '../../../api/axios/APIV1';
import PaytmIframeModal from './PaytmIframeModal';

const SubscriptionPaymentPage = () => {
    const { subcriptionId } = useParams();
    const [showPaytmIframe, setShowPaytmIframe] = useState(false);
    const [paytmIframeUrl, setPaytmIframeUrl] = useState('');
    const [loading, setLoading] = useState(false);
    const [subscriptionData, setsubscriptionData] = useState(null);
    const [fetchingStatus, setFetchingStatus] = useState(false);
    const [paymentError, setPaymentError] = useState(null);
    const [showPaymentUI, setShowPaymentUI] = useState(false);
    const [cancellingSubscription, setCancellingSubscription] = useState(false);
    const [renewingSubscription, setRenewingSubscription] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [renewSuccessMessage, setRenewSuccessMessage] = useState('');
    const selectedRestaurantId = useSelector((state) => state.auth.selectedRestaurantId);

    // Add this helper function at the top of your component file
    const getPlanName = (days) => {
        console.log(`days ==>`, days);
        if (!days) return 'Plan name is not Required';

        if (days <= '28 Days') {
            return 'Monthly';
        } else if (days <= '90 Days') {
            return 'Quarterly';
        } else if (days <= '180 Days') {
            return 'Half Yearly';
        } else if (days <= '365 Days') {
            return 'Yearly';
        }
        return 'N/A';
    };

    // Determine if we're in staging environment
    const isStaging = false;

    // Store subscription ID in localStorage for retry functionality
    useEffect(() => {
        if (subcriptionId) {
            localStorage.setItem('lastSubscriptionId', subcriptionId);
        }
    }, [subcriptionId]);

    const fetchSubscriptionStatus = async (subId) => {
        if (!subId) {
            console.log('No subscription ID provided to fetchSubscriptionStatus');
            return;
        }

        setFetchingStatus(true);
        setPaymentError(null);

        try {
            const detailsResult = await APIV1.get(`/subscription/get-details?subscription_id=${subId}`);

            if (detailsResult?.success) {
                setsubscriptionData(detailsResult);

                const subscriptionStatus = detailsResult?.payment_details?.status?.toUpperCase();
                const lastOrderStatus = detailsResult?.payment_details?.lastOrderStatus?.toUpperCase();
                if (
                    subscriptionStatus === 'PENDING' ||
                    subscriptionStatus === 'INIT' ||
                    lastOrderStatus === 'PENDING' ||
                    lastOrderStatus === 'INIT'
                ) {
                    setShowPaymentUI(true);
                } else {
                    setShowPaymentUI(false);
                }
            } else {
                setPaymentError('Failed to fetch subscription details');
                console.error('Failed to fetch subscription details:', detailsResult?.message);
            }
        } catch (error) {
            console.error('Error fetching subscription details:', error);
            setPaymentError('Error occurred while fetching subscription details');
        } finally {
            setFetchingStatus(false);
        }
    };

    useEffect(() => {
        if (subcriptionId) {
            fetchSubscriptionStatus(subcriptionId);
        }
    }, [subcriptionId]);

    useEffect(() => {
        const addPaytmCSS = () => {
            const existingStyle = document.getElementById('paytm-fix-styles');
            if (existingStyle) return;
            const style = document.createElement('style');
            style.id = 'paytm-fix-styles';
            style.textContent = `
                iframe[src*="paytm"], iframe[src*="securegw"], iframe[src*="securestage"] {
                    pointer-events: auto !important; z-index: 999999 !important; position: relative !important; transform: none !important;
                }
                iframe[src*="paytm"] input, iframe[src*="securegw"] input, iframe[src*="securestage"] input,
                div[id*="paytm"] input, div[class*="paytm"] input {
                    pointer-events: auto !important; -webkit-user-select: auto !important; user-select: auto !important;
                    -webkit-touch-callout: auto !important; -webkit-tap-highlight-color: transparent !important;
                }
                .paytm-overlay, [class*="paytm"], [id*="paytm"] {
                    pointer-events: auto !important; z-index: auto !important;
                }
                input:focus { outline: auto !important; }
            `;
            document.head.appendChild(style);
        };
        addPaytmCSS();
        return () => {
            const style = document.getElementById('paytm-fix-styles');
            if (style) style.remove();
        };
    }, []);

    // Load Paytm script for staging
    function loadScript(src) {
        return new Promise((resolve) => {
            // Check if script already exists
            const existingScript = document.querySelector(`script[src="${src}"]`);
            if (existingScript) {
                resolve(true);
                return;
            }

            // Remove any existing Paytm scripts to prevent conflicts
            const oldScripts = document.querySelectorAll('script[src*="paytm"]');
            oldScripts.forEach((script) => script.remove());

            const script = document.createElement('script');
            script.type = 'application/javascript';
            script.src = src;
            script.crossOrigin = 'anonymous';
            script.async = false; // Force synchronous loading

            script.onload = () => {
                console.log('Paytm script loaded successfully');
                resolve(true);
            };

            script.onerror = (error) => {
                console.error('Failed to load Paytm script:', error);
                resolve(false);
            };

            document.head.appendChild(script); // Use head instead of body
        });
    }

    // Handle payment success for staging popup
    async function handlePaymentSuccess(paymentData, outletId) {
        try {
            console.log('Payment successful for subscription:', subcriptionId);
            console.log('Payment data:', paymentData);

            // TODO: Verify payment with your backend
            // const verifyResponse = await APIV1.post('/subscription/verify-payment', {
            //     subscription_id: subcriptionId,
            //     payment_data: paymentData
            // });

            alert('Payment successful! Your subscription is now active.');

            // Refresh subscription data
            await fetchSubscriptionStatus(subcriptionId);
        } catch (error) {
            console.error('Error handling payment success:', error);
            alert('Payment was successful but there was an error updating your subscription. Please contact support.');
        }
    }

    // Staging Paytm Script method
    async function displayPaytmScript() {
        try {
            // Load Paytm script if not already loaded
            const paytmScriptUrl = `https://securestage.paytmpayments.com/merchantpgpui/checkoutjs/merchants/${process.env.REACT_APP_PAYTM_MID}.js`;
            const res = await loadScript(paytmScriptUrl);
            if (!res) {
                alert('Paytm SDK failed to load. Please check your internet connection.');
                return;
            }

            // Get payment data from API
            let apiUrl = `subscription/initiate?restaurant_id=${selectedRestaurantId}`;
            if (subcriptionId) {
                apiUrl += `&subscription_id=${subcriptionId}`;
            }

            const result = await APIV1.post(apiUrl);
            if (!result || !result.data) {
                setPaymentError('Server error! Unable to initiate payment.');
                return;
            }

            // Destructure the data from the API response
            const { data: responseData } = result;
            const { order_id: orderId, transaction_token: token } = responseData;

            // Initialize Paytm configuration
            const config = {
                root: '',
                flow: 'DEFAULT',
                data: {
                    orderId: orderId,
                    token: token,
                    tokenType: 'TXN_TOKEN',
                    amount: subscriptionData?.subscription_information?.amount || '1.00',
                },
                merchant: {
                    mid: process.env.REACT_APP_PAYTM_MID,
                },
                handler: {
                    notifyMerchant: function (eventName, data) {
                        console.log('Paytm Event:', eventName);
                        console.log('Event Data:', data);

                        switch (eventName) {
                            case 'APP_CLOSED':
                                console.log('User closed the popup');
                                setPaymentError('Payment was cancelled. Please try again.');
                                break;
                            case 'TXN_SUCCESS':
                                console.log('Transaction successful!');
                                alert('Subscription payment successful!');
                                handlePaymentSuccess(data, subcriptionId);
                                break;
                            case 'TXN_FAILURE':
                                console.log('Transaction failed!');
                                alert('Subscription payment failed!');
                                setPaymentError('Payment failed. Please try again.');
                                break;
                            default:
                                console.log('Other event:', eventName);
                        }
                    },
                },
            };

            if (window.Paytm && window.Paytm.CheckoutJS) {
                // Use onLoad callback as per Paytm documentation
                window.Paytm.CheckoutJS.onLoad(function excecuteAfterCompleteLoad() {
                    console.log('Paytm SDK loaded successfully');
                    // Initialize configuration using init method
                    window.Paytm.CheckoutJS.init(config)
                        .then(function onSuccess() {
                            console.log('Paytm Checkout initialized successfully');
                            // After successfully updating configuration, invoke JS Checkout
                            window.Paytm.CheckoutJS.invoke();
                        })
                        .catch(function onError(error) {
                            console.error('Paytm initialization error:', error);
                            alert('Failed to initialize payment. Please try again.');
                            setPaymentError('Failed to initialize payment. Please try again.');
                        });
                });
            } else {
                console.error('Paytm CheckoutJS not loaded');
                alert('Payment system not loaded. Please refresh the page.');
                setPaymentError('Payment system not loaded. Please refresh the page.');
            }
        } catch (error) {
            console.error('Error in displayPaytmScript:', error);
            alert('Failed to initiate payment. Please try again.');
            setPaymentError('Failed to initiate payment. Please try again.');
        }
    }

    // Production Paytm Iframe method
    async function displayPaytmIframe() {
        setLoading(true);
        setPaymentError(null);
        try {
            let apiUrl = `subscription/initiate`;
            if (subcriptionId) {
                apiUrl += `?subscription_id=${subcriptionId}`;
            }

            const result = await APIV1.post(apiUrl);
            if (!result || !result.data) {
                setPaymentError('Server error! Unable to initiate payment.');
                setLoading(false);
                return;
            }

            // Debug the response structure
            console.log('Payment initiation response:', result.data);

            const { order_id: orderId, transaction_token: token } = result.data;
            console.log(`orderId ==>`, orderId, token);
            // Validate the values
            if (!orderId || !token) {
                throw new Error('Missing orderId or transaction token in response');
            }

            // URL encode the values
            const encodedOrderId = encodeURIComponent(orderId);
            const encodedToken = encodeURIComponent(token);
            const iframeUrl = `https://secure.paytmpayments.com/theia/api/v1/showPaymentPage?mid=${encodeURIComponent(
                process.env.REACT_APP_PAYTM_MID
            )}&orderId=${encodedOrderId}&txnToken=${encodedToken}`;

            console.log('Constructed Paytm URL:', iframeUrl);

            setPaytmIframeUrl(iframeUrl);
            setShowPaytmIframe(true);
        } catch (error) {
            console.error('Error in displayPaytm:', error);
            setPaymentError(error.message || 'Failed to initiate payment. Please try again.');
        } finally {
            setLoading(false);
        }
    }

    const displayPaytm = async () => {
        setLoading(true);
        setPaymentError(null);

        try {
            // Option 1: For staging environment - use popup script method
            if (isStaging) {
                console.log('Using staging Paytm popup method');
                await displayPaytmScript();
            }
            // Option 2: For production environment - use iframe method
            else {
                console.log('Using production Paytm iframe method');
                await displayPaytmIframe();
            }
        } catch (error) {
            console.error('Error in displayPaytm:', error);
            setPaymentError('Failed to initiate payment. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const cancelSubscription = async () => {
        if (!subcriptionId) {
            alert('No subscription ID available to cancel');
            return;
        }
        setCancellingSubscription(true);
        try {
            const response = await APIV1.post(`/subscription/cancel?subscription_id=${subcriptionId}`);
            if (response?.success) {
                alert('Subscription cancelled successfully!');
                await fetchSubscriptionStatus(subcriptionId);
            } else {
                alert(response?.message || 'Failed to cancel subscription');
            }
        } catch (error) {
            alert('Error occurred while cancelling subscription. Please try again.');
        } finally {
            setCancellingSubscription(false);
        }
    };

    const renewSubscription = async () => {
        if (!subcriptionId) {
            alert('No subscription ID available to renew');
            return;
        }
        setRenewingSubscription(true);
        try {
            const response = await APIV1.post(`/subscription/renew?subscription_id=${subcriptionId}`);
            if (response?.success) {
                const outletName = subscriptionData?.subscription_information?.outlet_name || 'Outlet';
                setRenewSuccessMessage(`Subscription for ${outletName} has been renewed successfully!`);
                setShowSuccessModal(true);
                await fetchSubscriptionStatus(subcriptionId);
            } else {
                alert(response?.message || 'Failed to renew subscription');
            }
        } catch (error) {
            console.error('Error renewing subscription:', error);
            alert('Error occurred while renewing subscription. Please try again.');
        } finally {
            setRenewingSubscription(false);
        }
    };

    const handlePaytmCallback = (response) => {
        if (response?.STATUS === 'TXN_SUCCESS') {
            setShowPaytmIframe(false);
            window.location.href = `${window.location.pathname}?paymentStatus=success&orderId=${
                response.ORDERID
            }&txnId=${response.TXNID}&subsId=${subcriptionId || ''}`;
        } else if (response?.STATUS === 'TXN_FAILURE') {
            setShowPaytmIframe(false);
            setPaymentError('Payment failed. Please try again.');
        } else if (response?.STATUS === 'PENDING') {
            setShowPaytmIframe(false);
            setPaymentError('Payment is pending. Please check your payment status later.');
        }
    };

    useEffect(() => {
        const handleMessage = (event) => {
            if (
                event.origin.includes('paytm') ||
                event.origin.includes('securegw') ||
                event.origin.includes('securestage')
            ) {
                if (event.data?.action === 'paymentResponse' || event.data?.STATUS) {
                    handlePaytmCallback(event.data);
                }
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return '--';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    if (fetchingStatus) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#F8F9FA' }}>
                <div style={{ backgroundColor: '#184D78', height: '100px' }}></div>
                <EuiPageBody restrictWidth='900px' style={{ marginTop: '-80px' }}>
                    <EuiPanel paddingSize='l' hasShadow={true} style={{ borderRadius: '6px' }}>
                        <EuiFlexGroup justifyContent='center' alignItems='center' style={{ minHeight: '400px' }}>
                            <EuiFlexItem grow={false}>
                                <EuiLoadingSpinner size='l' />
                                <EuiSpacer size='m' />
                                <EuiText size='s' color='subdued' textAlign='center'>
                                    {fetchingStatus
                                        ? 'Initializing subscription...'
                                        : 'Loading subscription details...'}
                                </EuiText>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </EuiPanel>
                </EuiPageBody>
            </div>
        );
    }

    if (subscriptionData && !showPaymentUI) {
        return (
            <div style={{ minHeight: '100vh', backgroundColor: '#F8F9FA' }}>
                <div style={{ backgroundColor: '#184D78', height: '100px' }}></div>

                <EuiPageBody restrictWidth='900px' style={{ marginTop: '-80px' }}>
                    <EuiPanel paddingSize='l' hasShadow={true} style={{ borderRadius: '6px' }}>
                        <EuiFlexGroup alignItems='center' gutterSize='m'>
                            <EuiFlexItem grow={false}>
                                <img
                                    src='https://api.app.fab.delivery/images/levaral_logo.png'
                                    alt='Levaral Logo'
                                    style={{ height: '60px', width: '60px', borderRadius: '4px' }}
                                />
                            </EuiFlexItem>
                            <EuiFlexItem>
                                <EuiText size='xs' color='subdued'>
                                    Outlet Subscription Details
                                </EuiText>
                                <EuiText>
                                    <span style={{ fontSize: '1.25rem' }}>{'Outlet'}</span>
                                </EuiText>
                            </EuiFlexItem>
                        </EuiFlexGroup>

                        <EuiHorizontalRule margin='l' />

                        {subscriptionData && (
                            <div
                                style={{
                                    backgroundColor:
                                        subscriptionData.payment_details.status?.toUpperCase() === 'ACTIVE'
                                            ? '#d4edda'
                                            : ['CLOSED', 'REJECT', 'EXPIRED', 'SUSPENDED'].includes(
                                                    subscriptionData.payment_details.status?.toUpperCase()
                                                )
                                              ? '#f8d7da'
                                              : '#fff3cd',
                                    border:
                                        subscriptionData.payment_details.status?.toUpperCase() === 'ACTIVE'
                                            ? '1px solid #c3e6cb'
                                            : ['CLOSED', 'REJECT', 'EXPIRED', 'SUSPENDED'].includes(
                                                    subscriptionData.payment_details?.status?.toUpperCase()
                                                )
                                              ? '1px solid #f5c6cb'
                                              : '1px solid #ffeeba',
                                    padding: '12px 16px',
                                    borderRadius: '4px',
                                    marginBottom: '24px',
                                }}
                            >
                                <EuiText
                                    style={{
                                        color:
                                            subscriptionData.payment_details?.status?.toUpperCase() === 'ACTIVE'
                                                ? '#155724'
                                                : ['CLOSED', 'REJECT', 'EXPIRED', 'SUSPENDED'].includes(
                                                        subscriptionData.payment_details?.status?.toUpperCase()
                                                    )
                                                  ? '#721c24'
                                                  : '#856404',
                                    }}
                                >
                                    <strong>Subscription Status:</strong>{' '}
                                    {(subscriptionData.payment_details?.status || 'Unknown').toUpperCase()}
                                </EuiText>
                            </div>
                        )}

                        <EuiFlexGroup>
                            {/* Left Column - Subscription Details */}
                            <EuiFlexItem>
                                <EuiText size='s'>
                                    <h4>Subscription Information</h4>
                                </EuiText>
                                <EuiSpacer size='m' />

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        OUTLET NAME
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.subscription_information?.outlet_name || 'N/A'}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        SUBSCRIPTION ID
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.subscription_information?.subscription_id ||
                                            subcriptionId ||
                                            '--'}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        SUBSCRIPTION TYPE
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.payment_details?.payment_method || '--'}
                                    </EuiText>
                                </div>

                                {/* <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        FREQUENCY
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.frequency || '--'} {subscriptionData?.frequencyUnit || ''}
                                    </EuiText>
                                </div> */}

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        SUBSCRIPTION AMOUNT
                                    </EuiText>
                                    <EuiText size='s'>
                                        ₹ {subscriptionData?.payment_details?.paid_amount || '--'}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        PLAN NAME
                                    </EuiText>
                                    <EuiText size='s'>
                                        {getPlanName(subscriptionData?.subscription_information?.plan_name) || 'N/A'}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        TOTAL PURCHASES
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.subscription_information?.total_purchase || '--'}
                                    </EuiText>
                                </div>
                            </EuiFlexItem>

                            {/* Right Column - Payment Details */}
                            <EuiFlexItem>
                                <EuiText size='s'>
                                    <h4>Payment Details</h4>
                                </EuiText>
                                <EuiSpacer size='m' />

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        SUBSCRIPTION STATUS
                                    </EuiText>
                                    <EuiText
                                        size='s'
                                        style={{
                                            color:
                                                subscriptionData?.payment_details?.status?.toUpperCase() === 'ACTIVE'
                                                    ? '#28a745'
                                                    : ['CLOSED', 'REJECT', 'EXPIRED', 'SUSPENDED'].includes(
                                                            subscriptionData?.payment_details?.status?.toUpperCase()
                                                        )
                                                      ? '#dc3545'
                                                      : '#6c757d',
                                        }}
                                    >
                                        {(subscriptionData?.payment_details?.status || '--').toUpperCase()}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        START DATE
                                    </EuiText>
                                    <EuiText size='s'>
                                        {formatDate(subscriptionData?.payment_details?.start_date || '--')}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        RENEWAL DATE
                                    </EuiText>
                                    <EuiText size='s'>
                                        {formatDate(subscriptionData?.payment_details?.renew_date || '--')}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        NEXT PURCHASE DATE
                                    </EuiText>
                                    <EuiText size='s'>
                                        {formatDate(
                                            subscriptionData?.subscription_information?.next_purchase_date || '--'
                                        )}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        PAYMENT METHOD
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.payment_details?.payment_method || '--'}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        PAYER NUMBER
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.payment_details?.payer_number || '--'}
                                    </EuiText>
                                </div>

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        PAID AMOUNT
                                    </EuiText>
                                    <EuiText size='s'>
                                        ₹ {subscriptionData?.payment_details?.paid_amount || '--'}
                                    </EuiText>
                                </div>

                                {/* <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        LAST ORDER DATE
                                    </EuiText>
                                    <EuiText size='s'>
                                        {formatDate(
                                            subscriptionData?.lastOrderCreationDate ||
                                                subscriptionData?.paymentDetails?.lastSuccessTxnDate
                                        )}
                                    </EuiText>
                                </div> */}
                                {/* 
                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        LAST ORDER ID
                                    </EuiText>
                                    <EuiText size='s'>
                                        {subscriptionData?.lastOrderId ||
                                            subscriptionData?.paymentDetails?.lastSuccessOrderId ||
                                            '--'}
                                    </EuiText>
                                </div> */}

                                {subscriptionData?.subscription_information?.amount && (
                                    <div style={{ marginBottom: '16px' }}>
                                        <EuiText size='xs' color='subdued'>
                                            UPFRONT TRANSACTION AMOUNT
                                        </EuiText>
                                        <EuiText size='s'>
                                            ₹ {subscriptionData?.subscription_information?.amount}
                                        </EuiText>
                                    </div>
                                )}
                            </EuiFlexItem>
                        </EuiFlexGroup>

                        {/* Cancel Subscription Button - Only show for ACTIVE subscriptions */}
                        {subscriptionData?.payment_details?.status?.toUpperCase() === 'ACTIVE' && (
                            <>
                                <EuiSpacer size='l' />
                                <EuiFlexGroup justifyContent='flexEnd'>
                                    <EuiFlexItem grow={false}>
                                        <EuiButton
                                            color='danger'
                                            isLoading={cancellingSubscription}
                                            onClick={() => {
                                                if (
                                                    window.confirm('Are you sure you want to cancel this subscription?')
                                                ) {
                                                    cancelSubscription();
                                                }
                                            }}
                                        >
                                            {cancellingSubscription ? 'Cancelling...' : 'Cancel Subscription'}
                                        </EuiButton>
                                    </EuiFlexItem>
                                </EuiFlexGroup>
                            </>
                        )}
                        <EuiHorizontalRule margin='xl' />

                        <EuiSpacer size='l' />

                        {/* Footer */}
                        <EuiFlexGroup justifyContent='center' alignItems='center'>
                            <EuiFlexItem grow={false}>
                                <EuiText size='xs' color='subdued'>
                                    Subscription payments are powered by
                                </EuiText>
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <img
                                    src='https://assetscdn1.paytm.com/images/catalog/category/5165/paytm_logo.png'
                                    alt='Paytm'
                                    style={{ height: '30px' }}
                                />
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <EuiText size='xs' color='subdued'>
                                    . To start accepting online payments, visit{' '}
                                    <EuiLink href='https://business.paytm.com/' target='_blank' color='primary'>
                                        Paytm.com
                                    </EuiLink>
                                </EuiText>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </EuiPanel>
                </EuiPageBody>
                <EuiSpacer size='xxl' />
            </div>
        );
    }
    const planeName = getPlanName(subscriptionData?.subscription_information?.plan_name);
    const outletName = subscriptionData?.subscription_information?.outlet_name ?? 'N/A';
    const displayPlanName = `${planeName} Store Subscription (${outletName})`;
    const displayAmount = subscriptionData?.subscription_information?.amount ?? '0';
    const displayDueDate =
        formatDate(subscriptionData?.subscription_information?.next_purchase_date) ||
        formatDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
    const displayPaymentMethod = subscriptionData?.payment_details?.payment_method || 'NetBanking/Card/UPI';

    return (
        <div style={{ minHeight: '100vh', backgroundColor: '#F8F9FA' }}>
            <div style={{ backgroundColor: '#184D78', height: '100px' }}></div>

            <EuiPageBody restrictWidth='900px' style={{ marginTop: '-80px' }}>
                <EuiPanel paddingSize='l' hasShadow={true} style={{ borderRadius: '6px' }}>
                    {/* Company Header */}
                    <EuiFlexGroup alignItems='center' gutterSize='m'>
                        <EuiFlexItem grow={false}>
                            <img
                                src='https://api.app.fab.delivery/images/levaral_logo.png'
                                alt='Levaral Logo'
                                style={{ height: '60px', width: '60px', borderRadius: '4px' }}
                            />
                        </EuiFlexItem>
                        <EuiFlexItem>
                            <EuiText size='xs' color='subdued'>
                                Your Subscription at
                            </EuiText>
                            <EuiText>
                                <span className='eui-textBold eui-fontSizeL'>LEVARAL SOFTWARES PRIVATE LIMITED</span>
                            </EuiText>
                            {/* Environment indicator for staging */}
                            {isStaging && (
                                <EuiText size='xs' style={{ color: '#f39c12', fontWeight: 'bold' }}>
                                    [STAGING ENVIRONMENT - Popup Payment]
                                </EuiText>
                            )}
                        </EuiFlexItem>
                    </EuiFlexGroup>

                    <EuiHorizontalRule margin='l' />

                    {paymentError && (
                        <div
                            style={{
                                backgroundColor: '#f8d7da',
                                border: '1px solid #f5c6cb',
                                padding: '12px 16px',
                                borderRadius: '4px',
                                marginBottom: '24px',
                            }}
                        >
                            <EuiText style={{ color: '#721c24' }}>
                                <strong>Payment Error:</strong> {paymentError}
                            </EuiText>
                        </div>
                    )}

                    <EuiFlexGroup gutterSize='xl'>
                        {/* Left Column - Subscription Details */}
                        <EuiFlexItem>
                            <EuiText size='xs' color='subdued'>
                                <strong>SUBSCRIPTION PLAN</strong>
                            </EuiText>
                            <EuiSpacer size='m' />

                            <EuiFlexGroup justifyContent='spaceBetween' alignItems='flexStart'>
                                <EuiFlexItem>
                                    <EuiText size='m'>
                                        <p style={{ fontWeight: 500, margin: '0 0 8px 0' }}>{displayPlanName}</p>
                                    </EuiText>
                                    <EuiText size='s' color='subdued'>
                                        ₹ {displayAmount} × 1 unit
                                    </EuiText>
                                </EuiFlexItem>
                                <EuiFlexItem grow={false} style={{ textAlign: 'right' }}>
                                    <EuiText size='m'>
                                        <p style={{ fontWeight: 500, margin: 0 }}>₹ {displayAmount}</p>
                                    </EuiText>
                                    <EuiText size='s' color='subdued'>
                                        Billed for this {planeName}
                                    </EuiText>
                                </EuiFlexItem>
                            </EuiFlexGroup>

                            <EuiSpacer size='xxl' />

                            {subscriptionData?.subscription_information?.next_purchase_date && (
                                <div
                                    style={{
                                        backgroundColor: 'rgba(22, 163, 74, 0.05)',
                                        border: '1px solid rgba(22, 163, 74, 0.2)',
                                        padding: '12px 16px',
                                        borderRadius: '6px',
                                        marginBottom: '24px',
                                    }}
                                >
                                    <EuiText size='s' style={{ color: '#16a34a', fontWeight: 500 }}>
                                        Complete your payment to activate the subscription. Due on {displayDueDate}.
                                    </EuiText>
                                </div>
                            )}

                            <EuiText size='xs' color='subdued'>
                                For any queries, please contact LEVARAL SOFTWARES PRIVATE LIMITED.
                            </EuiText>
                        </EuiFlexItem>

                        {/* Right Column - Payment Details */}
                        <EuiFlexItem grow={false} style={{ width: '250px' }}>
                            <EuiPanel
                                paddingSize='l'
                                style={{ backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #e5e7eb' }}
                            >
                                <EuiText size='s'>
                                    <strong>Payment Details</strong>
                                </EuiText>
                                <EuiSpacer size='l' />

                                <div style={{ marginBottom: '16px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        DUE ON
                                    </EuiText>
                                    <EuiText size='s'>{displayDueDate}</EuiText>
                                </div>

                                <div style={{ marginBottom: '24px' }}>
                                    <EuiText size='xs' color='subdued'>
                                        PAID USING
                                    </EuiText>
                                    <EuiText size='s'>{displayPaymentMethod}</EuiText>
                                </div>

                                <EuiButton
                                    fill
                                    fullWidth
                                    onClick={displayPaytm}
                                    isLoading={loading}
                                    style={{ backgroundColor: '#184D78' }}
                                >
                                    Proceed to Pay
                                </EuiButton>

                                {['SUSPENDED', 'REJECT', 'EXPIRED'].includes(
                                    subscriptionData?.payment_details?.status?.toUpperCase()
                                ) && (
                                    <>
                                        <EuiSpacer size='m' />
                                        <EuiButton
                                            fill
                                            fullWidth
                                            color='primary'
                                            onClick={renewSubscription}
                                            isLoading={renewingSubscription}
                                        >
                                            {renewingSubscription ? 'Renewing...' : 'Renew Subscription'}
                                        </EuiButton>
                                    </>
                                )}
                            </EuiPanel>
                        </EuiFlexItem>
                    </EuiFlexGroup>

                    <EuiHorizontalRule margin='xl' />

                    {/* Footer */}
                    <EuiFlexGroup justifyContent='center' alignItems='center'>
                        <EuiFlexItem grow={false}>
                            <EuiText size='xs' color='subdued'>
                                Subscription payments are powered by
                            </EuiText>
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <img
                                src='https://assetscdn1.paytm.com/images/catalog/category/5165/paytm_logo.png'
                                alt='Paytm'
                                style={{ height: '30px' }}
                            />
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiText size='xs' color='subdued'>
                                . To start accepting online payments, visit{' '}
                                <EuiLink href='https://business.paytm.com/' target='_blank' color='primary'>
                                    Paytm.com
                                </EuiLink>
                            </EuiText>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiPanel>

                {/* Show PaytmIframeModal only for production */}
                {!isStaging && showPaytmIframe && (
                    <PaytmIframeModal
                        isOpen={showPaytmIframe}
                        onClose={() => {
                            setShowPaytmIframe(false);
                            setPaymentError('Payment was cancelled. Please try again.');
                        }}
                        iframeUrl={paytmIframeUrl}
                        onPaymentCallback={handlePaytmCallback}
                    />
                )}

                {/* Success Modal for Renew Subscription */}
                {showSuccessModal && (
                    <EuiModal onClose={() => setShowSuccessModal(false)} initialFocus='[name=popswitch]'>
                        <EuiModalHeader>
                            <EuiModalHeaderTitle>
                                <h2>Subscription Renewed Successfully!</h2>
                            </EuiModalHeaderTitle>
                        </EuiModalHeader>

                        <EuiModalBody>
                            <EuiText>
                                <p>{renewSuccessMessage}</p>
                            </EuiText>
                        </EuiModalBody>

                        <EuiModalFooter>
                            <EuiButton
                                fill
                                color='primary'
                                onClick={() => {
                                    setShowSuccessModal(false);
                                    // history.push('/subscription');
                                    // Optionally redirect or refresh
                                }}
                                name='popswitch'
                            >
                                OK
                            </EuiButton>
                        </EuiModalFooter>
                    </EuiModal>
                )}
            </EuiPageBody>
            <EuiSpacer size='xxl' />
        </div>
    );
};

export default SubscriptionPaymentPage;
